import React from "react";
import { Link } from "react-router-dom";
import { Heart } from "lucide-react";
import { useApp } from "../context/AppContext";
import { formatPrice } from "../i18n/translations";

const ProductCard = ({ product, index = 0 }) => {
  const { t, language, currency, toggleWishlist, wishlist, addToCart } = useApp();
  const isDE = language === "de";
  const name = isDE ? product.name_de : product.name_en;
  const onSale = product.compare_at_price && product.compare_at_price > product.price;
  const inWish = wishlist.includes(product.id);
  const oos = !product.in_stock;
  const slug = isDE && product.slug_de ? product.slug_de : product.slug;

  return (
    <Link
      to={`/product/${slug}`}
      className="group block reveal"
      style={{ animationDelay: `${index * 0.06}s` }}
      data-testid={`product-card-${product.slug}`}
    >
      <div className="relative img-zoom bg-[var(--ivory-deep)] aspect-[4/5] overflow-hidden">
        <img
          src={product.images[0]}
          alt={name}
          className="w-full h-full object-cover"
          loading="lazy"
        />
        {product.images[1] && (
          <img
            src={product.images[1]}
            alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-700"
            loading="lazy"
          />
        )}
        <div className="absolute top-4 left-4 flex flex-col gap-1.5">
          {oos && (
            <span className="bg-[var(--noir)] text-[var(--gold)] text-[10px] tracking-[0.2em] uppercase px-2.5 py-1 font-medium" data-testid={`badge-coming-soon-${product.ref}`}>
              {t.comingSoon}
            </span>
          )}
          {onSale && !oos && (
            <span className="bg-[var(--gold)] text-[var(--noir)] text-[10px] tracking-[0.2em] uppercase px-2.5 py-1 font-medium">
              Sale
            </span>
          )}
        </div>
        <button
          onClick={(e) => { e.preventDefault(); toggleWishlist(product.id); }}
          className="absolute top-4 right-4 w-9 h-9 bg-white/90 backdrop-blur-sm flex items-center justify-center hover:bg-white transition-colors"
          data-testid={`wish-${product.ref}`}
          aria-label="Wishlist"
        >
          <Heart className={`w-4 h-4 ${inWish ? "fill-[var(--noir)] text-[var(--noir)]" : "text-[var(--noir)]"}`} />
        </button>

        {!oos && (
          <div className="absolute bottom-0 left-0 right-0 translate-y-full group-hover:translate-y-0 transition-transform duration-500">
            <button
              onClick={(e) => { e.preventDefault(); addToCart(product.id); }}
              className="w-full bg-[var(--noir)] text-[var(--ivory)] py-3.5 text-[11px] tracking-[0.25em] uppercase hover:text-[var(--gold)]"
              data-testid={`quick-add-${product.ref}`}
            >
              + {t.product.addToCart}
            </button>
          </div>
        )}
      </div>

      <div className="pt-5 pb-2">
        <h3 className="font-serif text-lg leading-snug text-[var(--noir)] group-hover:text-[var(--gold-deep)] transition-colors line-clamp-2">
          {name}
        </h3>
        <div className="flex items-baseline gap-2 mt-1.5">
          <span className="text-sm text-[var(--noir)]">{formatPrice(product.price, currency)}</span>
          {onSale && (
            <span className="text-xs text-[var(--grey)] line-through">{formatPrice(product.compare_at_price, currency)}</span>
          )}
        </div>
        {product.reviews_count > 0 && (
          <div className="flex items-center gap-1 mt-2 text-[10px] tracking-[0.15em] uppercase text-[var(--grey)]">
            <span className="text-[var(--gold)]">★★★★★</span>
            <span>{product.rating.toFixed(1)} ({product.reviews_count})</span>
          </div>
        )}
      </div>
    </Link>
  );
};

export default ProductCard;
